import React, { useEffect, useState } from 'react';
import {
  TagIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import {
  thunkFetchCouponDashboardStats,
  thunkFetchExpiringCoupons,
  thunkFetchRecentCouponUsage,
  thunkCreateCoupon,
  thunkFetchCoupons
} from '../../redux/slices/couponSlice';
import CouponForm from './CouponForm';
import { useNavigate } from 'react-router-dom';
import { useAdminLayout } from "../../context/adminLayoutUseContext";
const CouponDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    dashboardStats,
    expiringCoupons,
    recentActivity,
    coupons,
    loading,
    error
  } = useSelector(state => state.coupon);
  const { setLayoutConfig } = useAdminLayout();
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  useEffect(() => {
    dispatch(thunkFetchCouponDashboardStats());
    dispatch(thunkFetchExpiringCoupons(7));
    dispatch(thunkFetchRecentCouponUsage(5));
    dispatch(thunkFetchCoupons({ page_size: 10, ordering: '-created_at' }));
  }, [dispatch]);

  const handleNewCoupon = () => {
    setEditingCoupon(null);
    setShowCouponForm(true);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setShowCouponForm(true);
  };

  const handleSaveCoupon = async (formData) => {
    await dispatch(thunkCreateCoupon(formData));
    setShowCouponForm(false);
    setEditingCoupon(null);
    dispatch(thunkFetchCoupons({ page_size: 10, ordering: '-created_at' }));
    dispatch(thunkFetchCouponDashboardStats());
    dispatch(thunkFetchExpiringCoupons(7));
  };

  // Quick Links handlers
  const handleGoToManagement = () => navigate('/admin/coupon/management');
  const handleGoToAnalytics = () => navigate('/admin/coupon/analytics');
  const handleGoToBulkGenerate = () => navigate('/admin/coupon/bulk-generate');
  useEffect(() => {
    setLayoutConfig({
      title: "Coupon Dashboard",
      showBackButton: false,
      scrollable: true,
    });

  }, [setLayoutConfig]);


  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Coupon Dashboard</h1>
            <p className="text-gray-600 mt-2">Overview of coupon performance and activity</p>
          </div>
          <button
            onClick={handleNewCoupon}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Coupon
          </button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <button
          onClick={handleGoToManagement}
          className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors w-full"
        >
          <TagIcon className="h-6 w-6 text-blue-600 mr-3" />
          <span className="font-medium text-blue-800">Coupon Management</span>
          <ArrowRightIcon className="h-4 w-4 ml-auto text-blue-400" />
        </button>
        <button
          onClick={handleGoToAnalytics}
          className="flex items-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors w-full"
        >
          <ChartBarIcon className="h-6 w-6 text-orange-600 mr-3" />
          <span className="font-medium text-orange-800">Coupon Analytics</span>
          <ArrowRightIcon className="h-4 w-4 ml-auto text-orange-400" />
        </button>
        <button
          onClick={handleGoToBulkGenerate}
          className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors w-full"
        >
          <ArrowPathIcon className="h-6 w-6 text-green-600 mr-3" />
          <span className="font-medium text-green-800">Bulk Generate</span>
          <ArrowRightIcon className="h-4 w-4 ml-auto text-green-400" />
        </button>
      </div>

      {/* Dashboard Stats */}
      {dashboardStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center">
            <TagIcon className="h-6 w-6 text-blue-600 mr-4" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Coupons</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_coupons}</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-green-600 mr-4" />
            <div>
              <p className="text-sm font-medium text-gray-600">Active Coupons</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardStats.active_coupons}</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center">
            <UsersIcon className="h-6 w-6 text-purple-600 mr-4" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Uses</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_uses}</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center">
            <CurrencyDollarIcon className="h-6 w-6 text-yellow-600 mr-4" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Discount</p>
              <p className="text-2xl font-bold text-gray-900">${dashboardStats.total_discount_given}</p>
            </div>
          </div>
        </div>
      )}

      {/* Expiring Coupons */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2" />
          Expiring Soon
        </h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          {expiringCoupons && expiringCoupons.length > 0 ? (
            <ul>
              {expiringCoupons.map(coupon => (
                <li key={coupon.id} className="flex justify-between py-2 border-b last:border-b-0">
                  <span className="font-mono">{coupon.code}</span>
                  <span>{new Date(coupon.end_date).toLocaleDateString()}</span>
                  <button
                    className="text-blue-600 hover:underline ml-4"
                    onClick={() => handleEditCoupon(coupon)}
                  >
                    Edit
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No coupons expiring soon.</p>
          )}
        </div>
      </div>

      {/* Recent Coupon Usage */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 flex items-center">
          <Cog6ToothIcon className="h-5 w-5 text-blue-500 mr-2" />
          Recent Coupon Usage
        </h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          {recentActivity && recentActivity.length > 0 ? (
            <ul>
              {recentActivity.map(activity => (
                <li key={activity.id} className="flex justify-between py-2 border-b last:border-b-0">
                  <span className="font-mono">{activity.coupon_code}</span>
                  <span>{activity.user_email}</span>
                  <span>{new Date(activity.used_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No recent coupon usage.</p>
          )}
        </div>
      </div>

      {/* Recent Coupons Table */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 flex items-center">
          <ArrowPathIcon className="h-5 w-5 text-green-500 mr-2" />
          Recent Coupons
        </h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {coupons && coupons.map(coupon => (
                <tr key={coupon.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-mono">{coupon.code}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{coupon.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{coupon.coupon_type_display}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{coupon.discount_value}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{new Date(coupon.end_date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {coupon.is_expired ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Expired</span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      className="text-indigo-600 hover:underline"
                      onClick={() => handleEditCoupon(coupon)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Coupon Form Modal */}
      <CouponForm
        isOpen={showCouponForm}
        coupon={editingCoupon}
        onSave={handleSaveCoupon}
        onCancel={() => {
          setShowCouponForm(false);
          setEditingCoupon(null);
        }}
      />
    </div>
  );
};

export default CouponDashboard;